import React, { useState, useEffect, useReducer, useContext, useRef, useCallback } from "react";
import openSocket from "socket.io-client";
import { aoReconectar } from "../../helpers/reconexaoSocket";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItem";
import TicketsListSkeleton from "../TicketsListSkeleton";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { emitTicketMovedToTop } from "../../helpers/notificationBus";
import {
	LISTA_VAZIA,
	ticketCabeNaLista,
	ehFiltroPorTempo,
	mesclarPagina,
	aplicarTicket,
	removerTicket,
	marcarLido,
	atualizarContato,
} from "../../helpers/listaTickets";

const useStyles = makeStyles(theme => ({
	ticketsListWrapper: {
		position: "relative",
		display: "flex",
		height: "100%",
		flexDirection: "column",
		overflow: "hidden",
		borderTopRightRadius: 0,
		borderBottomRightRadius: 0,
	},

	ticketsList: {
		flex: 1,
		overflowY: "scroll",
		// sem isso o overflow-x virava "auto" (regra do CSS quando um eixo é
		// scroll) e qualquer item mais largo criava barra horizontal
		overflowX: "hidden",
		...theme.scrollbarStyles,
		borderTop: "2px solid rgba(0, 0, 0, 0.12)",
	},

	ticketsListHeader: {
		color: "var(--text-secondary)",
		zIndex: 2,
		backgroundColor: "var(--surface-1)",
		borderBottom: "1px solid var(--border)",
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
	},

	ticketsCount: {
		fontWeight: "normal",
		color: "rgb(104, 121, 146)",
		marginLeft: "8px",
		fontSize: "14px",
	},

	noTicketsText: {
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},
}));

// Filtro de tempo ("aguardando mais que 30m"...): o ticket passa a caber sem
// evento nenhum, então a lista se rebusca sozinha nesse intervalo.
const INTERVALO_FILTRO_TEMPO_MS = 60 * 1000;
// Aba do navegador que ficou escondida por mais que isso rebusca ao voltar.
const AUSENCIA_PARA_REBUSCAR_MS = 2 * 60 * 1000;
// Rebuscas do topo: a espera junta a rajada (cada socket reconecta num instante
// diferente) e o intervalo é o mínimo entre o início de duas.
const ESPERA_RESSINCRONIZAR_MS = 1000;
const INTERVALO_MIN_RESSINCRONIZAR_MS = 10 * 1000;
// Contagem da aba (GET /tickets/contagem): no máximo uma a cada 30 s por lista.
const INTERVALO_MIN_CONTAGEM_MS = 30 * 1000;

const mesmosTickets = (a, b) => {
	if (a.length !== b.length) return false;
	const ids = new Set(a.map(t => t.id));
	return b.every(t => ids.has(t.id));
};

// `buscasTopo` e `mudancasSocket` só existem para disparar a recontagem: a 1ª página
// mudou, ou o socket pôs ou tirou conversa da lista. O socket não diz se o ticket que
// entrou já estava no total (conversa da página 7 que recebeu mensagem sobe pro topo),
// então não dá para somar ou subtrair: conta de novo.
const comMudancaSocket = (estado, novo) => (mesmosTickets(estado.tickets, novo.tickets)
	? novo
	: { ...novo, mudancasSocket: (estado.mudancasSocket || 0) + 1 });

const reducer = (estado, action) => {
	switch (action.type) {
		case "PAGINA": {
			const novo = mesclarPagina(estado, action.pagina, action);
			return action.modo === "mais" ? novo : { ...novo, buscasTopo: (estado.buscasTopo || 0) + 1 };
		}
		case "TICKET":
			return comMudancaSocket(estado, aplicarTicket(estado, action.ticket, action.cabe, action));
		case "REMOVER":
			return comMudancaSocket(estado, removerTicket(estado, action.ticketId, action.quando));
		case "LIDO":
			return comMudancaSocket(estado, marcarLido(estado, action.ticketId, action.avaliar, action.quando));
		case "CONTATO":
			return atualizarContato(estado, action.contato);
		case "RESET":
			return LISTA_VAZIA;
		default:
			return estado;
	}
};

const urlsDoSocket = user => {
	const normalizar = u => (u || "").trim().replace(/\/+$/, "");
	const urls = new Set([normalizar(process.env.REACT_APP_BACKEND_URL)]);
	(user.whatsapps || [])
		.map(w => normalizar(w.urlBackend))
		.filter(Boolean)
		.forEach(url => urls.add(url));
	return [...urls];
};

// Socket e buscas leem filtro, cursor e relógio de refs, não da closure: trocar de
// filtro não reabre os sockets e nenhum evento é julgado com o filtro anterior. Cada
// troca de filtro abre uma geração nova, e resposta de geração anterior que chegue
// atrasada é descartada. relogioRef é a diferença servidor − navegador: os filtros
// de tempo valem no relógio do SQL, e navegador com relógio adiantado existe.
const TicketsList = (props) => {
	const { status, searchParam, showAll, selectedQueueIds, selectedWhatsappIds, selectedUserIds, selectedFilialIds, selectedTime, selectedTag, dataInicio, dataFim, dataAgendamento, updateCount, style, onSelectTicket, onMenuContexto, selectedItems: selectedItemsProp } =
		props;
	const classes = useStyles();
	const [estado, dispatch] = useReducer(reducer, LISTA_VAZIA);
	const ticketsList = estado.tickets;
	const hasMore = estado.temMais;
	const [loading, setLoading] = useState(true);
	const [contagem, setContagem] = useState(null);
	const { user } = useContext(AuthContext);
	const selectedItems = selectedItemsProp || [];

	const contextoRef = useRef({});
	contextoRef.current = {
		filtros: { status, selectedQueueIds, selectedWhatsappIds, selectedUserIds, selectedFilialIds, selectedTime, selectedTag, dataInicio, dataFim },
		searchParam,
		showAll,
		dataAgendamento,
		user,
	};
	const geracaoRef = useRef(0);
	const estadoRef = useRef(estado);
	estadoRef.current = estado;
	const paginaRef = useRef(1);
	const carregandoMaisRef = useRef(false);
	const relogioRef = useRef(0);
	const ordenarPorNaoLidasRef = useRef(false);

	const agoraServidor = () => new Date(Date.now() + relogioRef.current);
	const avaliar = ticket => ticketCabeNaLista(ticket, contextoRef.current.filtros, {
		user: contextoRef.current.user,
		agora: agoraServidor(),
	});

	// "inicio": 1ª página de um filtro novo. "mais": a página depois do cursor, na
	// rolagem. "ressincronizar": rebusca o topo sem limpar a tela. Atendente e filial
	// só vão com seleção parcial — nada ou tudo marcado chega null do TicketsManager.
	// O pageNumber fica de reserva para backend que ainda não devolve cursor.
	const parametrosDoFiltro = () => {
		const { filtros: f, searchParam: busca, showAll: todos, dataAgendamento: agendamento } = contextoRef.current;
		return {
			searchParam: busca,
			status: f.status,
			showAll: todos,
			queueIds: JSON.stringify(f.selectedQueueIds),
			whatsappIds: JSON.stringify(f.selectedWhatsappIds),
			userIds: Array.isArray(f.selectedUserIds) && f.selectedUserIds.length > 0
				? JSON.stringify(f.selectedUserIds)
				: undefined,
			filialIds: Array.isArray(f.selectedFilialIds) && f.selectedFilialIds.length > 0
				? JSON.stringify(f.selectedFilialIds)
				: undefined,
			selectedTime: f.selectedTime,
			selectedTag: f.selectedTag,
			dataInicio: f.dataInicio,
			dataFim: f.dataFim,
			dataAgendamento: agendamento,
		};
	};

	const buscar = useCallback(async modo => {
		const geracao = geracaoRef.current;
		const cursorPedido = modo === "mais" ? estadoRef.current.cursor : null;
		const paginaPedida = modo === "mais" ? paginaRef.current + 1 : 1;
		if (modo === "mais") {
			if (carregandoMaisRef.current || !estadoRef.current.temMais) return;
			carregandoMaisRef.current = true;
			setLoading(true);
		}
		try {
			const { data } = await api.get("/tickets", {
				params: {
					...parametrosDoFiltro(),
					cursor: cursorPedido ? JSON.stringify(cursorPedido) : undefined,
					pageNumber: cursorPedido ? undefined : paginaPedida,
				},
			});
			if (geracao !== geracaoRef.current) return;
			if (data.consultadoEm) {
				relogioRef.current = new Date(data.consultadoEm).getTime() - Date.now();
			}
			ordenarPorNaoLidasRef.current = !!data.ordenarPorNaoLidas;
			dispatch({
				type: "PAGINA",
				modo,
				pagina: data.tickets || [],
				cursorPedido,
				cursorRecebido: data.hasMore ? (data.cursor || null) : null,
				temMais: !!data.hasMore,
				consultadoEm: data.consultadoEm,
				ordenarPorNaoLidas: ordenarPorNaoLidasRef.current,
			});
			if (modo !== "ressincronizar") paginaRef.current = paginaPedida;
		} catch (err) {
			if (geracao === geracaoRef.current && modo !== "ressincronizar") toastError(err);
		} finally {
			if (modo === "mais") carregandoMaisRef.current = false;
			if (geracao === geracaoRef.current && modo !== "ressincronizar") setLoading(false);
		}
	}, []);

	// Todo pedido de rebusca do topo passa por aqui (reconexão de cada socket, volta pra
	// aba, filtro de tempo). A lista tem um socket por URL de backend — 14 na Muniz — e
	// uma queda de rede reconecta todos: sai no máximo uma busca por vez, com intervalo
	// mínimo. Pedido durante uma busca vira uma rebusca depois dela (a consulta em
	// andamento pode ser anterior ao que se perdeu). Aba escondida espera a volta: é nela
	// que o navegador mais derruba socket, e ninguém está olhando.
	const ressincRef = useRef({ timer: null, emAndamento: false, pendente: false, aoVoltar: false, ultimaEm: 0, desmontado: false });
	const pedirRessincronizar = useCallback(() => {
		const r = ressincRef.current;
		if (r.desmontado) return;
		if (document.hidden) {
			r.aoVoltar = true;
			return;
		}
		if (r.emAndamento) {
			r.pendente = true;
			return;
		}
		if (r.timer) return;
		const espera = Math.max(ESPERA_RESSINCRONIZAR_MS, r.ultimaEm + INTERVALO_MIN_RESSINCRONIZAR_MS - Date.now());
		r.timer = setTimeout(async () => {
			r.timer = null;
			r.emAndamento = true;
			r.ultimaEm = Date.now();
			try {
				await buscar("ressincronizar");
			} finally {
				r.emAndamento = false;
				if (r.pendente) {
					r.pendente = false;
					pedirRessincronizar();
				}
			}
		}, espera);
	}, [buscar]);

	// Só a aba Contatos conta, e só com página por carregar: sem ela a tela já é o
	// total. Mesmo freio do resync (uma por vez, pedido no meio vira uma depois), com
	// intervalo maior porque o socket pede a cada conversa que entra ou sai. Falha é
	// calada: o contador volta ao "N+".
	const contagemRef = useRef({ timer: null, emAndamento: false, pendente: false, ultimaEm: 0, desmontado: false });
	const pedirContagem = useCallback(() => {
		const c = contagemRef.current;
		if (c.desmontado || c.timer) return;
		if (c.emAndamento) {
			c.pendente = true;
			return;
		}
		const espera = Math.max(0, c.ultimaEm + INTERVALO_MIN_CONTAGEM_MS - Date.now());
		c.timer = setTimeout(async () => {
			c.timer = null;
			const { filtros, searchParam: busca } = contextoRef.current;
			if (filtros.status !== "open" || busca || document.hidden || !estadoRef.current.temMais) return;
			const geracao = geracaoRef.current;
			c.emAndamento = true;
			c.ultimaEm = Date.now();
			try {
				const { data } = await api.get("/tickets/contagem", { params: parametrosDoFiltro() });
				if (geracao === geracaoRef.current && !c.desmontado) {
					setContagem({ total: data.total, excedeu: !!data.excedeu });
				}
			} catch (err) {
				// o rótulo cai no "N+"
			} finally {
				c.emAndamento = false;
				if (c.pendente) {
					c.pendente = false;
					pedirContagem();
				}
			}
		}, espera);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (estado.buscasTopo || estado.mudancasSocket) pedirContagem();
	}, [estado.buscasTopo, estado.mudancasSocket, pedirContagem]);

	useEffect(() => () => {
		ressincRef.current.desmontado = true;
		clearTimeout(ressincRef.current.timer);
		contagemRef.current.desmontado = true;
		clearTimeout(contagemRef.current.timer);
	}, []);

	const chaveFiltros = JSON.stringify([status, searchParam, showAll, selectedQueueIds, selectedWhatsappIds, selectedUserIds, selectedFilialIds, selectedTime, selectedTag, dataInicio, dataFim, dataAgendamento]);

	// A 1ª página do filtro novo já cobre qualquer rebusca que estava agendada.
	useEffect(() => {
		geracaoRef.current += 1;
		paginaRef.current = 1;
		carregandoMaisRef.current = false;
		const r = ressincRef.current;
		clearTimeout(r.timer);
		r.timer = null;
		r.pendente = false;
		r.aoVoltar = false;
		const c = contagemRef.current;
		clearTimeout(c.timer);
		c.timer = null;
		c.pendente = false;
		c.ultimaEm = 0;
		setContagem(null);
		dispatch({ type: "RESET" });
		setLoading(true);
		const espera = setTimeout(() => buscar("inicio"), 500);
		return () => clearTimeout(espera);
	}, [chaveFiltros, buscar]);

	useEffect(() => {
		if (!ehFiltroPorTempo(selectedTime) || searchParam) return;
		const intervalo = setInterval(pedirRessincronizar, INTERVALO_FILTRO_TEMPO_MS);
		return () => clearInterval(intervalo);
	}, [selectedTime, searchParam, pedirRessincronizar]);

	useEffect(() => {
		let escondidaDesde = document.hidden ? Date.now() : null;
		const aoMudarVisibilidade = () => {
			if (document.hidden) {
				escondidaDesde = Date.now();
				return;
			}
			const r = ressincRef.current;
			const ficouFora = escondidaDesde && Date.now() - escondidaDesde >= AUSENCIA_PARA_REBUSCAR_MS;
			if (ficouFora || r.aoVoltar) {
				r.aoVoltar = false;
				pedirRessincronizar();
			}
			escondidaDesde = null;
		};
		document.addEventListener("visibilitychange", aoMudarVisibilidade);
		return () => document.removeEventListener("visibilitychange", aoMudarVisibilidade);
	}, [pedirRessincronizar]);

	const urlsSocket = user && user.id ? JSON.stringify(urlsDoSocket(user)) : "";

	// Reconexão rebusca o topo: o socket.io não reenvia o que se perdeu na queda (ver
	// helpers/reconexaoSocket). Em busca por texto a lista é só o resultado da
	// consulta. A notificação sai só quando a conversa entra ou sobe NESTA lista; o
	// resto (escopo, aceite, som) é do DesktopNotificationManager. avaliar e
	// agoraServidor leem só refs, por isso ficam fora das deps.
	useEffect(() => {
		if (!urlsSocket) {
			console.error("USUARIO NÃO ENCONTRADO NO SOCKET");
			return;
		}
		const sockets = JSON.parse(urlsSocket).map(url => {
			const socket = openSocket(url.trim() + "/");
			socket.on("connect", () => {
				if (status) {
					socket.emit("joinTickets", status);
				} else {
					socket.emit("joinNotification");
				}
			});

			aoReconectar(socket, pedirRessincronizar);

			const aplicar = ticket => {
				const cabe = avaliar(ticket);
				dispatch({
					type: "TICKET",
					ticket,
					cabe,
					ordenarPorNaoLidas: ordenarPorNaoLidasRef.current,
					quando: agoraServidor().getTime(),
				});
				return cabe;
			};

			socket.on("ticket", data => {
				if (contextoRef.current.searchParam) return;
				if (data.action === "updateUnread") {
					dispatch({ type: "LIDO", ticketId: data.ticketId, avaliar, quando: agoraServidor().getTime() });
				}
				if (data.action === "update" && data.ticket) {
					aplicar(data.ticket);
				}
				if (data.action === "delete") {
					dispatch({ type: "REMOVER", ticketId: data.ticketId, quando: agoraServidor().getTime() });
				}
			});

			socket.on("appMessage", data => {
				if (contextoRef.current.searchParam) return;
				if (data.action !== "create" || !data.ticket) return;
				if (aplicar(data.ticket)) emitTicketMovedToTop(data);
			});

			socket.on("contact", data => {
				if (contextoRef.current.searchParam) return;
				if (data.action === "update" && data.contact) {
					dispatch({ type: "CONTATO", contato: data.contact });
				}
			});
			return socket;
		});
		return () => {
			sockets.forEach(socket => socket.disconnect());
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status, urlsSocket, pedirRessincronizar]);

	useEffect(() => {
		if (typeof updateCount === "function") {
			updateCount(ticketsList.length, hasMore, contagem);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ticketsList.length, hasMore, contagem]);

	const handleScroll = e => {
		if (!hasMore || loading) return;

		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

		if (scrollHeight - (scrollTop + 100) < clientHeight) {
			buscar("mais");
		}
	};

	// const handleCheckboxChange = (id) => {
	// 	setSelectedItems((prevSelected) =>
	// 		prevSelected.includes(id)
	// 			? prevSelected.filter((item) => item !== id) // Remove se já estiver selecionado
	// 			: [...prevSelected, id] // Adiciona se não estiver selecionado
	// 	);
	// 	onSelectTicket(selectedItems);
	// 	console.log(id);
	// 	console.log(selectedItems.length);
	// };


	const handleCheckboxChange = (id) => {
		if (!onSelectTicket) return;
		const updatedItems = selectedItems.includes(id)
			? selectedItems.filter((item) => item !== id)
			: [...selectedItems, id];
		onSelectTicket(updatedItems);
	};

	return (
		<Paper className={classes.ticketsListWrapper} style={style}>
			<Paper
				square
				name="closed"
				elevation={0}
				className={classes.ticketsList}
				onScroll={handleScroll}
			>
				<List style={{ paddingTop: 0 }}>
					{ticketsList.length === 0 && !loading ? (
						<div className={classes.noTicketsDiv}>
							<span className={classes.noTicketsTitle}>
								{i18n.t("ticketsList.noTicketsTitle")}
							</span>
							<p className={classes.noTicketsText}>
								{i18n.t("ticketsList.noTicketsMessage")}
							</p>
						</div>
					) : (<>{ticketsList.map(ticket => (
						<TicketListItem
							ticket={ticket}
							key={ticket.id}
							selectedItems={selectedItems}
							// Só a lista que recebe onSelectTicket (aba Contatos) tem
							// checkbox: em Grupos e Aguardando ele aparecia mas não fazia
							// nada, porque handleCheckboxChange sai fora sem onSelectTicket.
							multiplaSelecao={dataAgendamento ? false : !!onSelectTicket}
							onCheckboxChange={handleCheckboxChange}
							// Menu de contexto (botão direito) só onde as ações em lote
							// existem — hoje a lista da aba Contatos. Quem não recebe
							// onMenuContexto mantém o menu do navegador, como era antes.
							onMenuContexto={onMenuContexto} />
					))}</>)}
					{loading && <TicketsListSkeleton />}
				</List>
			</Paper>
		</Paper>
	);
};

export default TicketsList;